import { ReactNode } from "react";

export type FaqItem = { q: string; a: ReactNode };
export type FaqSection = { title: string; items: FaqItem[] };
export type FaqContent = {
    title: string;
    subtitle: string;
    ui: { search: string; all: string; noResults: string; helpTitle: string; helpDesc: string; helpCta: string };
    sections: FaqSection[];
    contacts: { title: string; headers: [string, string, string]; rows: [string, string, string][] };
};

const APP_STORE = "https://apps.apple.com/sa/app/tygo-%D8%AA%D8%A7%D9%8A%D9%82%D9%88/id1619973559";
const GOOGLE_PLAY = "https://play.google.com/store/apps/details?id=app.tygo.sa&hl=en_US";

const Phone = ({ n }: { n: string }) => (
    <a href={`tel:${n.replace(/\s/g, "")}`} dir="ltr" className="faq-phone">{n}</a>
);

// "900 / 0114525525" -> two tel links; "—" stays plain text
const PhoneList = ({ value }: { value: string }) => {
    if (!/\d/.test(value)) return <span className="text-muted">{value}</span>;
    const numbers = value.split("/").map((n) => n.trim());
    return (
        <span className="faq-phones">
            {numbers.map((n) => <Phone key={n} n={n} />)}
        </span>
    );
};

const Table = ({ headers, rows, phones = false }: { headers: string[]; rows: string[][]; phones?: boolean }) => (
    <div className="table-responsive">
        <table className="table faq-table mb-0">
            <thead>
                <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>{r.map((c, j) => <td key={j}>{phones && j > 0 ? <PhoneList value={c} /> : c}</td>)}</tr>
                ))}
            </tbody>
        </table>
    </div>
);

export const faqAr: FaqContent = {
    title: "الأسئلة الشائعة",
    subtitle: "كل ما تحتاج معرفته عن تايقو، الشرائح والباقات، والدفع والدعم.",
    ui: {
        search: "ابحث عن سؤال...",
        all: "الكل",
        noResults: "لا توجد نتائج مطابقة. جرّب كلمة أخرى.",
        helpTitle: "لم تجد إجابتك؟",
        helpDesc: "فريق دعم تايقو جاهز لمساعدتك.",
        helpCta: "مركز المساعدة",
    },
    sections: [
        {
            title: "عن تايقو",
            items: [
                {
                    q: "ما هو تايقو؟",
                    a: <p>تايقو منصة رقمية معتمدة لدى هيئة الاتصالات والفضاء والتقنية (CST)، ووسيط لعدد من شركات الاتصالات في السعودية. تقارن من خلال التطبيق بين أفضل عروض الشرائح والباقات (المحلية والدولية) وتشتريها مباشرة.</p>,
                },
                {
                    q: "ما الشركات المتاحة حالياً؟",
                    a: <p>موبايلي، سلام، ريدبول، ياقوت، فيرجن، STC، ليبارا، فرندي. وسيتم إطلاق شركات وباقات أخرى قريباً.</p>,
                },
                {
                    q: "كيف أحمّل التطبيق؟",
                    a: (
                        <ul>
                            <li>آيفون: <a href={APP_STORE} target="_blank" rel="noreferrer">App Store</a></li>
                            <li>أندرويد: <a href={GOOGLE_PLAY} target="_blank" rel="noreferrer">Google Play</a></li>
                        </ul>
                    ),
                },
            ],
        },
        {
            title: "الشراء والتفعيل",
            items: [
                {
                    q: "ما المطلوب لاستخراج وتفعيل شريحة؟",
                    a: <p>يجب أن يكون لديك حساب فعّال في <strong>أبشر</strong> و<strong>نفاذ</strong>. إذا وُجدت مشكلة في أحدهما فلا يمكن إكمال التفعيل. إذا رغبت بالإلغاء بسبب نفاذ، تواصل مع مزود الخدمة أو دعم تايقو مع كامل التفاصيل.</p>,
                },
                {
                    q: "كم المدة المتاحة لتفعيل الشريحة؟",
                    a: <p><strong>30 يوماً</strong> من تاريخ الشراء. بعدها تصبح الشريحة غير صالحة.</p>,
                },
                {
                    q: "كم شريحة يمكنني استخراجها بهويتي؟",
                    a: (
                        <Table
                            headers={["الفئة", "الحد المسموح"]}
                            rows={[
                                ["سعودي", "10 شرائح مسبقة الدفع"],
                                ["مقيم (إقامة)", "5 شرائح (2 لبعض الشركات مثل ياقوت)"],
                                ["زائر / سائح", "2 شريحة"],
                            ]}
                        />
                    ),
                },
                {
                    q: "ما الفرق بين مسبق الدفع والمفوتر؟",
                    a: <p>المفوتر تصدر له فاتورة شهرية في نهاية كل دورة. مسبق الدفع تشحن رصيده مقدماً لتفعيل الباقات.</p>,
                },
                {
                    q: "هل يمكنني استخدام الشريحة في راوتر؟",
                    a: <p>لا. باقات تايقو مخصصة للهواتف الذكية فقط، ولا تعمل في الراوترات أو أجهزة الواي فاي المتنقلة.</p>,
                },
                {
                    q: "هل يمكنني نقل رقمي الحالي للاستفادة من العروض؟",
                    a: <p>نعم، إلى <strong>سلام أو ريدبول أو ياقوت</strong>. لا يمكن النقل إذا كانت شريحتك الحالية على نفس المشغل.</p>,
                },
                {
                    q: "أي شبكة (أبراج) تستخدم كل شركة؟",
                    a: <p>سلام وياقوت وريدبول على أبراج <strong>زين</strong>. فيرجن على أبراج <strong>STC</strong>.</p>,
                },
            ],
        },
        {
            title: "حلول المشاكل التقنية",
            items: [
                {
                    q: "تظهر رسالة \"الرمز غير صالح\" أو \"تعذر إضافة الباقة\" عند تثبيت eSIM",
                    a: (
                        <ol>
                            <li>أوقف الـ VPN.</li>
                            <li>اتصل بشبكة Wi-Fi قوية ومستقرة.</li>
                            <li>احذف أي eSIM قديم أو غير مستخدم من الإعدادات.</li>
                            <li>حدّث نظام الجهاز لآخر إصدار.</li>
                            <li>أعد تشغيل الجهاز كلياً وحاول مجدداً.</li>
                            <li>عند التثبيت اليدوي: اترك حقل رمز التأكيد فارغاً إن كان اختيارياً.</li>
                            <li>إن استمرت المشكلة، تواصل مع المشغل لإصدار QR جديد.</li>
                        </ol>
                    ),
                },
                {
                    q: "نقطة الاتصال (Hotspot) لا تعمل بعد التفعيل",
                    a: (
                        <ol>
                            <li>الإعدادات ← البيانات الخلوية.</li>
                            <li>اختر الشريحة الفعّالة.</li>
                            <li>اضغط <strong>شبكة البيانات الخلوية</strong>.</li>
                            <li>في حقل <strong>APN</strong> تحت <em>Personal Hotspot</em> اكتب: <code className="faq-code">globaldata</code></li>
                            <li>اترك اسم المستخدم وكلمة المرور فارغين.</li>
                        </ol>
                    ),
                },
                {
                    q: "هل أستطيع تثبيت eSIM على جهاز ثانٍ؟",
                    a: <p>لا. بعد مسح رمز QR وتثبيته على جهاز لا يمكن تثبيته على آخر. تواصل مع المزود لإصدار شريحة بديلة أو تحويلها إلى شريحة فعلية.</p>,
                },
                {
                    q: "كيف أحوّل بين eSIM والشريحة الفعلية؟",
                    a: <p>تواصل مباشرة مع مشغّلك.</p>,
                },
            ],
        },
        {
            title: "المدفوعات والاسترجاع والدعم",
            items: [
                {
                    q: "متى أستلم المبلغ المسترجع؟",
                    a: (
                        <>
                            <p>يُعاد المبلغ بنفس وسيلة الدفع:</p>
                            <ul>
                                <li><strong>مدى وSTC Pay:</strong> من يوم عمل إلى 5 أيام عمل.</li>
                                <li><strong>فيزا وماستركارد:</strong> من 7 إلى 14 يوم عمل.</li>
                            </ul>
                            <p>إن مرت المدة ولم يصلك المبلغ، راجع بنكك.</p>
                        </>
                    ),
                },
                {
                    q: "ما المعلومات المطلوبة لرفع طلب للدعم الفني؟",
                    a: (
                        <ol>
                            <li>رقم الجوال أو البريد الإلكتروني المسجل في تايقو.</li>
                            <li>رقم الهوية الوطنية أو الإقامة أو الجواز.</li>
                            <li>رقم الطلب.</li>
                            <li>لقطة شاشة للخطأ داخل التطبيق.</li>
                        </ol>
                    ),
                },
            ],
        },
        {
            title: "الشرائح الدولية",
            items: [
                {
                    q: "هل يوفر تايقو شرائح دولية؟",
                    a: <p>يوفر تايقو باقات <strong>eSIM دولية</strong> فقط (لا توجد شريحة فعلية للباقات الدولية). يمكنك مقارنتها وشراؤها مباشرة من التطبيق.</p>,
                },
                {
                    q: "كم عدد الدول المغطاة؟",
                    a: <p>أكثر من <strong>120 دولة</strong>. اختر وجهتك داخل التطبيق لعرض الباقات المتاحة.</p>,
                },
                {
                    q: "أين أجد الأسعار وحجم البيانات ومدة الصلاحية؟",
                    a: <p>جميع تفاصيل الباقات (السعر، البيانات، المدة) متوفرة داخل تطبيق تايقو لكل وجهة.</p>,
                },
                {
                    q: "متى تبدأ صلاحية الباقة الدولية؟",
                    a: <p>تبدأ الصلاحية <strong>عند الوصول</strong> إلى وجهتك.</p>,
                },
                {
                    q: "كيف أسترجع المبلغ أو أحصل على مساعدة في الباقة الدولية؟",
                    a: <p>تواصل مع دعم تايقو.</p>,
                },
                {
                    q: "هل تعمل الباقات الدولية في الراوتر؟",
                    a: <p>لا. باقات تايقو مخصصة للهواتف الذكية فقط، ولا تعمل في الراوترات أو أجهزة الواي فاي المتنقلة.</p>,
                },
                {
                    q: "واجهت مشكلة في تثبيت eSIM الدولي",
                    a: <p>تواصل مع دعم تايقو.</p>,
                },
                {
                    q: "نقطة الاتصال لا تعمل بعد التفعيل",
                    a: <p>تواصل مع دعم تايقو.</p>,
                },
            ],
        },
        {
            title: "أسئلة خاصة بكل مشغّل",
            items: [
                {
                    q: "موبايلي",
                    a: (
                        <>
                            <p><strong>ما تكلفة باقات موبايلي المفوترة؟</strong></p>
                            <ul>
                                <li><strong>باقة 200:</strong> رسوم إنشاء الطلب 57 ريال + تأمين 200 ريال (يُسترد أو يُخصم من الفواتير القادمة). التجديد: 115 ريال.</li>
                                <li><strong>باقة 400:</strong> رسوم إنشاء الطلب 57 ريال + تأمين 400 ريال (يُسترد أو يُخصم من الفواتير القادمة). التجديد: 230 ريال.</li>
                            </ul>
                            <p><strong>هل توجد سياسة استخدام عادل؟</strong></p>
                            <p>لا توجد على الباقات المفتوحة طالما لم تُصدر شرائح إضافية (Multi-SIM).</p>
                            <p><strong>أين أستلم شريحة نسك للحجاج؟</strong></p>
                            <p>من أكشاك موبايلي في مطار جدة أو المدينة (خارج صالات الحج والعمرة)، أو الفروع المخصصة في مكة والمدينة وجدة. شرائح نسك <strong>غير قابلة للاسترجاع</strong> وفق سياسة نسك الرسمية.</p>
                            <p><strong>دعم موبايلي:</strong> <Phone n="0560101100" /></p>
                        </>
                    ),
                },
                {
                    q: "ريدبول",
                    a: (
                        <ul>
                            <li><strong>الدعم:</strong> اتصال <Phone n="950" /> أو واتساب <Phone n="0575000950" /></li>
                            <li><strong>الأنواع:</strong> مسبقة الدفع ومفوترة، eSIM وشريحة فعلية.</li>
                            <li><strong>التوصيل:</strong> من 2 إلى 5 أيام عمل. توصيل سريع (يوم واحد) في المدن الكبرى: الرياض، بريدة، عنيزة، الرس، الخرج، شقراء، الدوادمي، جدة، مكة، الطائف، ينبع، الدمام، الخبر، الجبيل، الظهران، الأحساء، القطيف، المدينة، تبوك، حائل، سكاكا، خميس مشيط، أبها، نجران، الباحة.</li>
                            <li><strong>باقة 400+:</strong> تتضمن 3 شرائح.</li>
                        </ul>
                    ),
                },
                {
                    q: "سلام",
                    a: (
                        <ul>
                            <li><strong>الدعم:</strong> <Phone n="1101" /> | واتساب / دولي: <Phone n="+966511011101" /></li>
                            <li><strong>التوصيل:</strong> من 3 إلى 7 أيام عمل عبر مندوب سلام.</li>
                            <li><strong>العروض:</strong> خصم 50% لمدة سنة على باقات محددة. المفوتر يتطلب عقداً لمدة 3 أشهر.</li>
                            <li><strong>الاستخدام العادل:</strong> الباقة المفتوحة تُحدّد بـ 7 جيجا يومياً (تخفيض السرعة إلى 1 ميجابت) عند تفعيل Multi-SIM. باقة 179 بلا سياسة استخدام عادل.</li>
                        </ul>
                    ),
                },
                {
                    q: "ياقوت",
                    a: (
                        <ul>
                            <li><strong>الدعم:</strong> <Phone n="1711" /> | واتساب <Phone n="+966580171711" /></li>
                            <li><strong>النوع:</strong> eSIM وشرائح مسبقة الدفع رقمية.</li>
                            <li><strong>التوصيل:</strong> من 3 إلى 7 أيام عمل.</li>
                            <li><strong>الشحن والتجديد:</strong> عبر تطبيق ياقوت الرسمي.</li>
                        </ul>
                    ),
                },
                {
                    q: "فيرجن",
                    a: (
                        <ul>
                            <li><strong>الدعم:</strong> <Phone n="1789" /> | واتساب <Phone n="+966570001789" /></li>
                            <li><strong>عن فيرجن:</strong> مشغّل افتراضي (MVNO) على شبكة STC ويقدم باقات eSIM.</li>
                            <li><strong>التجديد:</strong> عبر تطبيق فيرجن موبايل.</li>
                        </ul>
                    ),
                },
                {
                    q: "STC",
                    a: (
                        <ul>
                            <li><strong>الدعم:</strong> <Phone n="900" /> أو <Phone n="0114525525" /></li>
                            <li><strong>النوع:</strong> باقات eSIM على تايقو.</li>
                            <li><strong>شروط العرض:</strong> الخصم لمدة 12 شهراً للعملاء الجدد مع تفعيل التجديد التلقائي. <strong>يُفقد الخصم</strong> إذا لم يكفِ الرصيد عند التجديد التلقائي، أو عند التحويل إلى شريحة فعلية، أو عند التجديد يدوياً عبر SMS.</li>
                        </ul>
                    ),
                },
            ],
        },
    ],
    contacts: {
        title: "جدول أرقام الدعم السريع",
        headers: ["المشغّل", "اتصال", "واتساب"],
        rows: [
            ["موبايلي", "0560101100", "—"],
            ["ريدبول", "950", "0575000950"],
            ["سلام", "1101", "+966511011101"],
            ["ياقوت", "1711", "+966580171711"],
            ["فيرجن", "1789", "+966570001789"],
            ["STC", "900 / 0114525525", "—"],
        ],
    },
};

export const faqEn: FaqContent = {
    title: "Frequently Asked Questions",
    subtitle: "Everything you need to know about TYGO, SIMs and packages, payments and support.",
    ui: {
        search: "Search for a question...",
        all: "All",
        noResults: "No matching questions. Try another word.",
        helpTitle: "Can't find your answer?",
        helpDesc: "The TYGO support team is ready to help.",
        helpCta: "Help Center",
    },
    sections: [
        {
            title: "About TYGO",
            items: [
                {
                    q: "What is TYGO?",
                    a: <p>TYGO is a digital platform licensed by the CST in Saudi Arabia and an intermediary for multiple telecom operators. Compare local and international SIM/eSIM packages in the app and buy them directly at competitive prices.</p>,
                },
                {
                    q: "Which providers are available?",
                    a: <p>Mobily, Salam, Red Bull Mobile, Yaqoot, Virgin Mobile, STC, Lebara and Friendi. More providers and packages are coming soon.</p>,
                },
                {
                    q: "How do I download the app?",
                    a: (
                        <ul>
                            <li>iPhone: <a href={APP_STORE} target="_blank" rel="noreferrer">App Store</a></li>
                            <li>Android: <a href={GOOGLE_PLAY} target="_blank" rel="noreferrer">Google Play</a></li>
                        </ul>
                    ),
                },
            ],
        },
        {
            title: "Purchase & Activation",
            items: [
                {
                    q: "What is required to activate a SIM/eSIM?",
                    a: <p>You must have an active <strong>Absher</strong> and <strong>Nafath</strong> account. If either has an issue, activation cannot proceed. To cancel because of Nafath issues, contact your provider or TYGO support with full details.</p>,
                },
                {
                    q: "How long do I have to activate?",
                    a: <p><strong>30 days</strong> from purchase. After that, the SIM becomes invalid.</p>,
                },
                {
                    q: "How many SIMs can I get per ID?",
                    a: (
                        <Table
                            headers={["Customer", "Limit"]}
                            rows={[
                                ["Saudi citizen", "10 prepaid SIMs"],
                                ["Resident (Iqama)", "5 SIMs (2 for some providers like Yaqoot)"],
                                ["Visitor / tourist", "2 SIMs"],
                            ]}
                        />
                    ),
                },
                {
                    q: "What is the difference between prepaid and postpaid?",
                    a: <p>Postpaid: a monthly bill is issued at the end of each billing period. Prepaid: you recharge in advance to activate packages.</p>,
                },
                {
                    q: "Can I use the SIM in a router?",
                    a: <p>No. TYGO packages are for smartphones only, not data-only routers or mobile Wi-Fi hotspots.</p>,
                },
                {
                    q: "Can I port my number to get offers?",
                    a: <p>Yes, to <strong>Salam, Red Bull or Yaqoot</strong>. Porting is not allowed if your current SIM is already with the same operator.</p>,
                },
                {
                    q: "Which network does each provider use?",
                    a: <p>Salam, Yaqoot and Red Bull use <strong>Zain</strong> towers. Virgin uses <strong>STC</strong> towers.</p>,
                },
            ],
        },
        {
            title: "Troubleshooting",
            items: [
                {
                    q: "\"Code Invalid\" or \"Cannot Add Cellular Plan\" error when installing an eSIM",
                    a: (
                        <ol>
                            <li>Turn off any VPN.</li>
                            <li>Use a strong, stable Wi-Fi connection.</li>
                            <li>Delete old or unused eSIM profiles from settings.</li>
                            <li>Update iOS/Android to the latest version.</li>
                            <li>Fully restart the device and retry.</li>
                            <li>For manual install, leave the confirmation code blank if optional.</li>
                            <li>If it persists, ask the provider to issue a new eSIM QR code.</li>
                        </ol>
                    ),
                },
                {
                    q: "Hotspot not working after activation",
                    a: (
                        <ol>
                            <li>Go to Settings → Cellular / Mobile Data.</li>
                            <li>Select the active SIM.</li>
                            <li>Tap <strong>Cellular Data Network</strong>.</li>
                            <li>In the <strong>APN</strong> field under <em>Personal Hotspot</em>, type: <code className="faq-code">globaldata</code></li>
                            <li>Leave Username and Password blank.</li>
                        </ol>
                    ),
                },
                {
                    q: "Can I install the eSIM on a second device?",
                    a: <p>No. Once the QR code is scanned and installed on one device it can't be used on another. Contact your provider for a replacement eSIM or a physical SIM.</p>,
                },
                {
                    q: "How do I switch between eSIM and physical SIM?",
                    a: <p>Contact your telecom operator directly.</p>,
                },
            ],
        },
        {
            title: "Payments, Refunds & Support",
            items: [
                {
                    q: "When will I receive my refund?",
                    a: (
                        <>
                            <p>Refunds go to the original payment method:</p>
                            <ul>
                                <li><strong>Mada &amp; STC Pay:</strong> 1 to 5 business days.</li>
                                <li><strong>Visa &amp; Mastercard:</strong> 7 to 14 business days.</li>
                            </ul>
                            <p>If the period has passed without receiving the funds, check with your bank.</p>
                        </>
                    ),
                },
                {
                    q: "What do I need to submit a support request?",
                    a: (
                        <ol>
                            <li>Mobile number or email registered in TYGO.</li>
                            <li>National ID, Iqama or Passport number.</li>
                            <li>Order number.</li>
                            <li>Screenshot of the exact error in the app.</li>
                        </ol>
                    ),
                },
            ],
        },
        {
            title: "International SIMs",
            items: [
                {
                    q: "Does TYGO offer international SIMs?",
                    a: <p>TYGO offers <strong>international eSIM</strong> packages only (there is no physical SIM for international). Compare and buy them directly in the app.</p>,
                },
                {
                    q: "How many countries are covered?",
                    a: <p>Over <strong>120 countries</strong>. Select your destination in the app to see available packages.</p>,
                },
                {
                    q: "Where can I find prices, data and validity?",
                    a: <p>All package details (price, data, validity) are shown in the TYGO app for each destination.</p>,
                },
                {
                    q: "When does the package validity start?",
                    a: <p>Validity starts <strong>on arrival</strong> at your destination.</p>,
                },
                {
                    q: "How do I get a refund or help with an international package?",
                    a: <p>Contact TYGO support.</p>,
                },
                {
                    q: "Can I use an international eSIM in a router?",
                    a: <p>No. TYGO packages are for smartphones only, not data-only routers or mobile Wi-Fi hotspots.</p>,
                },
                {
                    q: "My international eSIM won't install",
                    a: <p>Contact TYGO support.</p>,
                },
                {
                    q: "Hotspot not working after activation",
                    a: <p>Contact TYGO support.</p>,
                },
            ],
        },
        {
            title: "Operator-Specific FAQs",
            items: [
                {
                    q: "Mobily",
                    a: (
                        <>
                            <p><strong>What does Mobily postpaid cost?</strong></p>
                            <ul>
                                <li><strong>Plan 200:</strong> 57 SAR order fee + 200 SAR deposit (refunded or deducted from future bills). Renews at 115 SAR.</li>
                                <li><strong>Plan 400:</strong> 57 SAR order fee + 400 SAR deposit (refunded or deducted from future bills). Renews at 230 SAR.</li>
                            </ul>
                            <p><strong>Is there a fair usage policy?</strong></p>
                            <p>None on unlimited packages as long as no additional multi-SIM cards are issued.</p>
                            <p><strong>Where do I pick up a Nusuk SIM?</strong></p>
                            <p>At Mobily booths in Jeddah or Madinah airports (excluding Hajj/Umrah terminals) or designated branches in Makkah, Madinah and Jeddah. Nusuk SIMs are <strong>non-refundable</strong> per official Nusuk policy.</p>
                            <p><strong>Mobily support:</strong> <Phone n="0560101100" /></p>
                        </>
                    ),
                },
                {
                    q: "Red Bull Mobile",
                    a: (
                        <ul>
                            <li><strong>Support:</strong> call <Phone n="950" /> or WhatsApp <Phone n="0575000950" /></li>
                            <li><strong>Types:</strong> prepaid and postpaid, eSIM and physical SIM.</li>
                            <li><strong>Delivery:</strong> 2 to 5 business days. 1-day express delivery in major cities: Riyadh, Buraidah, Unaizah, Ar Rass, Al Kharj, Shaqra, Ad Dawadmi, Jeddah, Makkah, Taif, Yanbu, Dammam, Khobar, Jubail, Dhahran, Al Ahsa, Qatif, Madinah, Tabuk, Hail, Sakaka, Khamis Mushait, Abha, Najran, Al Baha.</li>
                            <li><strong>400+ package:</strong> includes 3 SIM cards.</li>
                        </ul>
                    ),
                },
                {
                    q: "Salam",
                    a: (
                        <ul>
                            <li><strong>Support:</strong> <Phone n="1101" /> | WhatsApp / international: <Phone n="+966511011101" /></li>
                            <li><strong>Delivery:</strong> 3 to 7 business days via Salam courier.</li>
                            <li><strong>Offers:</strong> 50% discount for 1 year on selected packages. Postpaid requires a 3-month contract.</li>
                            <li><strong>Fair usage:</strong> the unlimited package is limited to 7 GB/day (speed throttled to 1 Mbps) if multi-SIM is activated. Package 179 has no fair usage policy.</li>
                        </ul>
                    ),
                },
                {
                    q: "Yaqoot",
                    a: (
                        <ul>
                            <li><strong>Support:</strong> <Phone n="1711" /> | WhatsApp <Phone n="+966580171711" /></li>
                            <li><strong>Type:</strong> digital prepaid eSIMs and SIMs.</li>
                            <li><strong>Delivery:</strong> 3 to 7 business days.</li>
                            <li><strong>Recharge and renewal:</strong> through the official Yaqoot app.</li>
                        </ul>
                    ),
                },
                {
                    q: "Virgin Mobile",
                    a: (
                        <ul>
                            <li><strong>Support:</strong> <Phone n="1789" /> | WhatsApp <Phone n="+966570001789" /></li>
                            <li><strong>About:</strong> MVNO on the STC network offering eSIM packages.</li>
                            <li><strong>Renewal:</strong> through the Virgin Mobile app.</li>
                        </ul>
                    ),
                },
                {
                    q: "STC",
                    a: (
                        <ul>
                            <li><strong>Support:</strong> <Phone n="900" /> or <Phone n="0114525525" /></li>
                            <li><strong>Type:</strong> eSIM packages on TYGO.</li>
                            <li><strong>Offer terms:</strong> the 12-month discount is for new customers with auto-renewal on. It is lost if the balance is insufficient at auto-renewal, if converted to a physical SIM, or if renewed manually via SMS.</li>
                        </ul>
                    ),
                },
            ],
        },
    ],
    contacts: {
        title: "Quick Support Contacts",
        headers: ["Operator", "Call", "WhatsApp"],
        rows: [
            ["Mobily", "0560101100", "—"],
            ["Red Bull", "950", "0575000950"],
            ["Salam", "1101", "+966511011101"],
            ["Yaqoot", "1711", "+966580171711"],
            ["Virgin", "1789", "+966570001789"],
            ["STC", "900 / 0114525525", "—"],
        ],
    },
};

export { Table as FaqTable };
